import { supabase } from "@/integrations/supabase/client";
import { mapDbRowsToArticles, mapDbRowToArticle } from "@/lib/articleMapper";
import type { Article } from "@/types/article";
import { V2_CATEGORIES, type V2Category } from "./categories";

const PUBLIC_FIELDS =
  "id,title,excerpt,content,image_url,category,published_at,updated_at,source,trend_url,slug,sources,ai_summary,ai_bullets,merged_into,ai_political_lean,ai_lean_reasoning,ai_counter_perspective,ai_summary_qc,ai_political_check,ai_civic_scope_score,ai_political_scope,ai_scope_reason,ai_political_gate_passed,ai_gate_model,ai_input_source,ai_input_len,ai_summary_version,ai_analysis_model,photo_credit,author,confidence_level,category_signals,huvudamne,berorda_amnen,perspektiv,faktakaraktar,ai_processed_at";

const PUBLISHABLE_SUMMARY_MIN = 400;
const PUBLISHABLE_BULLETS_MIN = 4;

function isPublishableRow(row: any): boolean {
  if (!row) return false;
  if (!row.ai_processed_at) return false;
  if (!row.huvudamne || !row.faktakaraktar) return false;
  if (typeof row.ai_summary !== "string" || row.ai_summary.trim().length < PUBLISHABLE_SUMMARY_MIN) {
    return false;
  }
  if (!Array.isArray(row.ai_bullets) || row.ai_bullets.length < PUBLISHABLE_BULLETS_MIN) {
    return false;
  }
  return true;
}

function isPublishableSearchRow(row: any): boolean {
  if (!row) return false;
  if (!row.huvudamne || !row.faktakaraktar) return false;
  return typeof row.ai_summary === "string" && row.ai_summary.trim().length >= PUBLISHABLE_SUMMARY_MIN;
}

export async function fetchLatestArticles(limit = 30): Promise<Article[]> {
  const { data, error } = await supabase
    .from("articles")
    .select(PUBLIC_FIELDS)
    .in("category", V2_CATEGORIES as unknown as string[])
    .is("merged_into", null)
    .is("deleted_at", null)
    .not("ai_processed_at", "is", null)
    .not("ai_summary", "is", null)
    .not("huvudamne", "is", null)
    .not("faktakaraktar", "is", null)
    .neq("source", "Google Trends")
    .order("published_at", { ascending: false })
    .limit(Math.max(limit * 4, 60));
  if (error) throw error;
  return mapDbRowsToArticles((data ?? []).filter(isPublishableRow).slice(0, limit) as any);
}

export async function fetchCategoryArticles(
  category: V2Category,
  limit = 30,
): Promise<Article[]> {
  const { data, error } = await supabase
    .from("articles")
    .select(PUBLIC_FIELDS)
    .eq("category", category)
    .is("merged_into", null)
    .is("deleted_at", null)
    .not("ai_processed_at", "is", null)
    .not("ai_summary", "is", null)
    .not("huvudamne", "is", null)
    .not("faktakaraktar", "is", null)
    .neq("source", "Google Trends")
    .order("published_at", { ascending: false })
    .limit(Math.max(limit * 4, 60));
  if (error) throw error;
  return mapDbRowsToArticles((data ?? []).filter(isPublishableRow).slice(0, limit) as any);
}

export async function fetchTopicArticles(
  topic: string,
  limit = 40,
): Promise<Article[]> {
  const { data, error } = await supabase
    .from("articles")
    .select(PUBLIC_FIELDS)
    .contains("berorda_amnen", [topic])
    .in("category", V2_CATEGORIES as unknown as string[])
    .is("merged_into", null)
    .is("deleted_at", null)
    .not("ai_processed_at", "is", null)
    .not("ai_summary", "is", null)
    .not("huvudamne", "is", null)
    .not("faktakaraktar", "is", null)
    .order("published_at", { ascending: false })
    .limit(Math.max(limit * 4, 80));
  if (error) throw error;
  return mapDbRowsToArticles((data ?? []).filter(isPublishableRow).slice(0, limit) as any);
}

export async function fetchArticleBySlug(
  category: V2Category,
  slug: string,
): Promise<Article | null> {
  const { data, error } = await supabase
    .from("articles")
    .select(PUBLIC_FIELDS)
    .eq("category", category)
    .eq("slug", slug)
    .is("merged_into", null)
    .is("deleted_at", null)
    .maybeSingle();
  if (error) throw error;
  if (!data || !isPublishableRow(data)) return null;
  return mapDbRowToArticle(data as any);
}

/**
 * Fetch related articles in the same category, excluding the current one.
 * Used for internal linking on article pages to boost crawl depth.
 */
export async function fetchRelatedArticles(
  category: V2Category,
  excludeId: string,
  limit = 6,
): Promise<Article[]> {
  const { data, error } = await supabase
    .from("articles")
    .select(
      "id,title,category,slug,published_at,source,ai_summary,ai_bullets,ai_processed_at,excerpt,huvudamne,faktakaraktar",
    )
    .eq("category", category)
    .neq("id", excludeId)
    .is("merged_into", null)
    .is("deleted_at", null)
    .not("ai_processed_at", "is", null)
    .not("ai_summary", "is", null)
    .not("huvudamne", "is", null)
    .not("faktakaraktar", "is", null)
    .not("slug", "is", null)
    .order("published_at", { ascending: false })
    .limit(Math.max(limit * 3, 18));
  if (error) throw error;
  return mapDbRowsToArticles((data ?? []).filter(isPublishableRow).slice(0, limit) as any);
}

export interface TrendItem {
  id: string;
  title: string;
  source: string | null;
  sourceUrl: string | null;
  publishedAt: string | null;
  slug: string | null;
  excerpt: string | null;
}

/**
 * Trendar = Google Trends items, filtered to Swedish news sources
 * (source URL host ends with .se). No internal article page — we link out.
 */
export async function fetchTrendarItems(limit = 60): Promise<TrendItem[]> {
  const { data, error } = await supabase
    .from("articles")
    .select("id,title,source,sources,trend_url,published_at,slug,excerpt")
    .eq("category", "trendar")
    .is("merged_into", null)
    .is("deleted_at", null)
    .ilike("sources->0->>url", "%.se/%")
    .order("published_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return ((data ?? []) as any[]).map((r) => {
    const url: string | null =
      r?.sources?.[0]?.url ?? r?.trend_url ?? null;
    return {
      id: String(r.id),
      title: r.title,
      source: r.source ?? null,
      sourceUrl: url,
      publishedAt: r.published_at ?? null,
      slug: r.slug ?? null,
      excerpt: r.excerpt ?? null,
    };
  });
}

export interface TrendDetail {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  publishedAt: string | null;
  sources: { name: string | null; url: string }[];
  aiSummary: string | null;
  aiBullets: string[] | null;
  huvudamne: string | null;
  berordaAmnen: string[] | null;
  faktakaraktar: string | null;
  aiProcessedAt: string | null;
}

export async function fetchTrendBySlug(
  slug: string,
): Promise<TrendDetail | null> {
  const { data, error } = await supabase
    .from("articles")
    .select(
      "id,title,slug,excerpt,published_at,sources,trend_url,source,ai_summary,ai_bullets,huvudamne,berorda_amnen,faktakaraktar,ai_processed_at",
    )
    .eq("category", "trendar")
    .eq("slug", slug)
    .is("merged_into", null)
    .is("deleted_at", null)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const raw = (data as any).sources;
  let sources: { name: string | null; url: string }[] = [];
  if (Array.isArray(raw)) {
    sources = raw
      .filter((s: any) => s && typeof s.url === "string")
      .map((s: any) => ({ name: s.name ?? null, url: s.url }));
  }
  if (sources.length === 0 && (data as any).trend_url) {
    sources = [{ name: (data as any).source ?? null, url: (data as any).trend_url }];
  }
  return {
    id: String((data as any).id),
    title: (data as any).title,
    slug: (data as any).slug,
    excerpt: (data as any).excerpt ?? null,
    publishedAt: (data as any).published_at ?? null,
    sources,
    aiSummary: (data as any).ai_summary ?? null,
    aiBullets: Array.isArray((data as any).ai_bullets) ? (data as any).ai_bullets : null,
    huvudamne: (data as any).huvudamne ?? null,
    berordaAmnen: Array.isArray((data as any).berorda_amnen) ? (data as any).berorda_amnen : null,
    faktakaraktar: (data as any).faktakaraktar ?? null,
    aiProcessedAt: (data as any).ai_processed_at ?? null,
  };
}

export interface SearchHit {
  id: string;
  title: string;
  excerpt: string | null;
  category: string;
  slug: string | null;
  source: string | null;
  publishedAt: string | null;
  aiSummary: string | null;
  huvudamne: string | null;
  berordaAmnen: string[] | null;
  faktakaraktar: string | null;
  rank: number;
}

export async function searchArticles(
  q: string,
  opts: { category?: V2Category; topic?: string; limit?: number } = {},
): Promise<SearchHit[]> {
  const query = q.trim();
  if (!query) return [];
  const { data, error } = await supabase.rpc("search_articles", {
    q: query,
    cat: opts.category ?? null,
    topic: opts.topic ?? null,
    max_results: opts.limit ?? 30,
  });
  if (error) throw error;
  return ((data ?? []) as any[]).filter(isPublishableSearchRow).map((r) => ({
    id: String(r.id),
    title: r.title,
    excerpt: r.excerpt,
    category: r.category,
    slug: r.slug,
    source: r.source,
    publishedAt: r.published_at,
    aiSummary: r.ai_summary,
    huvudamne: r.huvudamne,
    berordaAmnen: r.berorda_amnen,
    faktakaraktar: r.faktakaraktar,
    rank: Number(r.rank ?? 0),
  }));
}